import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Author } from '@/types/article';
import { useToast } from '@/hooks/use-toast';

export function useAuthorManagement() {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const createAuthor = async (
    display_name: string, 
    bio?: string, 
    avatar_url?: string,
    social_links?: any,
    user_id?: string
  ): Promise<Author | null> => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('authors')
        .insert({
          display_name,
          bio: bio || null,
          avatar_url: avatar_url || null,
          social_links: social_links || {},
          user_id: user_id || null
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Success",
        description: "Author created successfully"
      });

      return data as Author;
    } catch (error) {
      console.error('Error creating author:', error);
      toast({
        title: "Error",
        description: "Failed to create author",
        variant: "destructive"
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateAuthor = async (
    id: string, 
    display_name: string, 
    bio?: string, 
    avatar_url?: string,
    social_links?: any,
    user_id?: string
  ): Promise<Author | null> => {
    setLoading(true);
    try {
      const updateData: any = {
        display_name,
        bio: bio || null,
        avatar_url: avatar_url || null,
        social_links: social_links || {},
        updated_at: new Date().toISOString()
      };
      
      // Only update user_id if explicitly provided
      if (user_id !== undefined) {
        updateData.user_id = user_id;
      }
      
      const { data, error } = await supabase
        .from('authors')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Success",
        description: "Author updated successfully"
      });

      return data as Author;
    } catch (error) {
      console.error('Error updating author:', error);
      toast({
        title: "Error",
        description: "Failed to update author",
        variant: "destructive"
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    createAuthor,
    updateAuthor,
    loading
  };
}